import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { ArrowRight, UserPlus, FileUp, Sparkles, ClipboardCheck, Handshake, Check, ShieldCheck, BadgeCheck } from "lucide-react";
import { PublicHeader } from "../components/PublicHeader";
import { SiteFooter } from "../components/SiteFooter";
import { ResumeReviewCard } from "../components/ResumeReviewCard";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";

const IMG = "https://images.unsplash.com/photo-1506973035872-a4ec16b8e8d9?auto=format&fit=crop&w=1200&q=80";
const COACHING_URL = "https://calendly.com/d/dz9w-ncv-nq6/stratergy-call-candiate-employment-support";

const steps = [
  { icon: UserPlus, title: "Create your account", time: "1 min", text: "Tell us where you are and where you want to be — we get your work rights sorted from day one, wherever you're coming from." },
  { icon: FileUp, title: "Upload your CV", time: "2 min", text: "Drop in your CV and we build out your experience, skills and qualifications into your profile automatically — ready for you to review and polish." },
  { icon: ClipboardCheck, title: "Complete your profile", time: "5–7 min", text: "Add your photo, credentials, references, ID and police check — everything employers need, in one place." },
  { icon: Handshake, title: "We place you", time: "Ongoing", text: "A recruiter personally matches and champions you to employers. We choose who we work with — and once you're in, we're in your corner." },
];

export default function CandidateLanding() {
  const navigate = useNavigate();
  const [payBusy, setPayBusy] = useState(false);

  const buyCvReview = async () => {
    if (payBusy) return;
    setPayBusy(true);
    try {
      const { data } = await api.post("/public/cv-review-checkout", { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e));
      setPayBusy(false);
    }
  };
  return (
    <div className="min-h-screen bg-[#fcfbf8]">
      <PublicHeader />
      <section className="pt-16 max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-12 items-center py-20">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-[#7C8514] font-medium">For candidates</p>
            <h1 className="text-3xl sm:text-5xl lg:text-6xl font-black uppercase text-[#0d2f4d] tracking-tighter leading-[0.98] mt-5">
              Looking for your next<br /><span className="font-accent font-medium normal-case italic text-[#7C8514]">opportunity in Australia?</span>
            </h1>
            <p className="mt-6 text-base md:text-lg text-[#51637a] max-w-lg leading-relaxed">
              Create your free candidate profile and tell us about your experience, career goals and what
              you're looking for in your next role. If we have a genuine vacancy that matches your
              background, our recruitment team may contact you.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-4">
              <Link to="/register" data-testid="candidate-landing-signup"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-[#C1CC24] text-[#0d2f4d] font-medium px-8 py-4 hover:bg-[#aeb81f] transition-colors">
                Create Your Free Candidate Profile <ArrowRight size={18} />
              </Link>
              <Link to="/signin" data-testid="candidate-landing-signin"
                className="inline-flex items-center justify-center rounded-full border border-[#0d2f4d] text-[#0d2f4d] font-medium px-8 py-4 hover:bg-[#0d2f4d] hover:text-white transition-colors">
                Sign in
              </Link>
            </div>
            <p className="mt-4 text-sm text-[#51637a] max-w-lg leading-relaxed" data-testid="free-registration-note">
              Free candidate registration. There is no fee to be considered for roles through The Ditch Careers.
            </p>
          </div>
          <div className="relative">
            <img src={IMG} alt="Sydney Harbour, Australia" className="rounded-lg object-cover w-full h-[420px]" />
            <div className="absolute -bottom-5 -left-5 bg-white rounded-lg border border-[#e3e0d3] px-5 py-4 shadow-lg hidden sm:block">
              <p className="flex items-center gap-2 text-sm font-medium text-[#0d2f4d]"><Sparkles size={16} className="text-[#7C8514]" /> Your CV, turned into a profile</p>
              <p className="text-xs text-[#51637a] mt-1">Upload once. We do the typing.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white border-y border-[#e3e0d3]">
        <div className="max-w-7xl mx-auto px-6 py-24">
          <h2 className="text-2xl sm:text-3xl font-bold text-[#0d2f4d] tracking-tight">How the process works</h2>
          <p className="font-accent text-lg text-[#7C8514] mt-3" data-testid="process-total-time">Under 10 minutes to get in front of a recruiter.</p>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map(({ icon: Icon, title, time, text }, i) => (
              <div key={title} className="relative">
                <span className="font-heading text-5xl font-extrabold text-[#f5f3ee] absolute -top-4 right-0">{`0${i + 1}`}</span>
                <Icon className="text-[#7C8514]" size={26} />
                <h3 className="font-heading font-bold text-[#0d2f4d] text-lg mt-4">
                  {title}
                  <span className="ml-2 align-middle inline-block rounded-full bg-[#C1CC24]/20 text-[#7C8514] text-xs font-semibold px-2.5 py-0.5" data-testid={`step-time-${i + 1}`}>{time}</span>
                </h3>
                <p className="text-sm text-[#51637a] mt-2 leading-relaxed">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-24" data-testid="cv-creator-section">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <p className="font-accent text-lg text-[#7C8514]">The CV creator</p>
            <h2 className="text-2xl sm:text-3xl font-bold text-[#0d2f4d] tracking-tight mt-2">Your CV does the typing. You do the approving.</h2>
            <p className="text-[#51637a] mt-4 leading-relaxed">
              Most job sites make you retype your entire career into endless forms. We don't. Upload your CV —
              PDF or Word — and our smart CV reader builds your full profile automatically. Every role, every
              skill, every qualification, structured and ready for a recruiter to champion.
            </p>
            <ul className="mt-6 space-y-3.5">
              {[
                ["Work history, extracted", "Job titles, employers, dates and responsibilities — pulled straight from your CV and laid out cleanly. Fix anything with one click."],
                ["Skills & qualifications, organised", "Your trade certificates, degrees, licences and skills become searchable tags our recruiters use to match you to live briefs."],
                ["You stay in control", "Nothing is submitted until you've reviewed it. Edit any section, add what your CV missed, and update it any time your situation changes."],
                ["Private until you say so", "Your profile is never public. Only our recruiters see it — and your blocked companies list means your current employer never will."],
              ].map(([t, d]) => (
                <li key={t} className="flex gap-3">
                  <Check size={17} className="text-[#7C8514] mt-1 shrink-0" />
                  <p className="text-sm text-[#122c47] leading-relaxed"><span className="font-medium text-[#0d2f4d]">{t}.</span> <span className="text-[#51637a]">{d}</span></p>
                </li>
              ))}
            </ul>
          </div>
          <div className="ditch-card p-7 bg-white" data-testid="cv-creator-mock-profile">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="h-11 w-11 rounded-full bg-[#0d2f4d] text-[#C1CC24] font-heading font-bold flex items-center justify-center">SM</span>
                <div>
                  <p className="font-heading font-bold text-[#0d2f4d]">Sarah Mitchell</p>
                  <p className="text-xs text-[#51637a]">Senior Chef · Auckland → Sydney</p>
                </div>
              </div>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-[#C1CC24]/20 text-[#7C8514] text-xs font-semibold px-3 py-1"><BadgeCheck size={13} /> AU work rights: eligible</span>
            </div>
            <div className="mt-5">
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#51637a] font-medium">Skills — extracted from CV</p>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {["Kitchen management", "Menu development", "Food safety (HACCP)", "Team leadership", "Cost control"].map((s) => (
                  <span key={s} className="rounded-full bg-[#f5f3ee] border border-[#e3e0d3] text-xs text-[#0d2f4d] px-3 py-1">{s}</span>
                ))}
              </div>
            </div>
            <div className="mt-5 space-y-3">
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#51637a] font-medium">Work history — extracted from CV</p>
              {[["Head Chef — The Wharf Kitchen, Auckland", "2021 – present"], ["Sous Chef — Hilton Queenstown", "2017 – 2021"]].map(([r, d]) => (
                <div key={r} className="border border-[#e3e0d3] rounded-lg px-4 py-3">
                  <p className="text-sm font-medium text-[#0d2f4d]">{r}</p>
                  <p className="text-xs text-[#51637a] mt-0.5">{d}</p>
                </div>
              ))}
            </div>
            <p className="flex items-center gap-2 text-xs text-[#51637a] mt-5 pt-4 border-t border-[#e3e0d3]">
              <Sparkles size={13} className="text-[#7C8514]" /> Built automatically from Sarah's CV in seconds — reviewed by Sarah, vetted by us.
            </p>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 pb-24" data-testid="resume-review-section">
        <p className="font-accent text-lg text-[#7C8514]">Just need your CV sorted?</p>
        <h2 className="text-2xl sm:text-3xl font-bold text-[#0d2f4d] tracking-tight mt-2">Not looking for recruitment help? No worries.</h2>
        <p className="text-[#51637a] mt-3 max-w-2xl leading-relaxed">
          You don't need to join the talent pool. If all you want is your CV edited, reviewed and brought up to
          Australian market standard, grab the Resume Review &amp; Builder on its own — pay securely, send us your
          CV, and it's back in your inbox within 48 hours, market-ready. No sign-up, no strings.
        </p>
        <div className="mt-8">
          <ResumeReviewCard testId="landing-resume-review-card"
            ctaLabel={payBusy ? "Redirecting..." : "Get my CV reviewed"}
            onAction={buyCvReview} />
        </div>
        <p className="text-sm text-[#51637a] mt-4 max-w-2xl leading-relaxed">
          <span className="font-medium text-[#0d2f4d]">Don't have a CV at all?</span> No problem — <Link to="/register" className="text-[#7C8514] font-medium hover:underline" data-testid="no-cv-signup-link">create your free profile</Link>, answer a few
          guided questions about your roles, and our team will build you a professional CV from scratch. Same price, same 48-hour turnaround.
        </p>
      </section>

      <section className="max-w-7xl mx-auto px-6 pb-24" data-testid="coaching-section">
        <div className="bg-[#0d2f4d] rounded-lg grain relative overflow-hidden p-8 sm:p-12">
          <div className="relative grid lg:grid-cols-2 gap-10 items-start">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full bg-[#C1CC24]/20 text-[#C1CC24] text-xs font-semibold uppercase tracking-[0.15em] px-3 py-1.5">
                <Sparkles size={13} /> The Ditch Collective · Optional extra support
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-white tracking-tight mt-5">Need help getting employment-ready?</h2>
              <p className="text-white/75 mt-4 leading-relaxed">
                Some people want more than a market-ready CV — they want a coach in their corner. The Ditch Collective's
                <span className="text-white font-medium"> 30-day Employment Preparation &amp; Coaching Package</span> is a
                separate, hands-on programme that gets you genuinely interview-ready and confident in the Australian market.
              </p>
              <div className="mt-7 flex items-baseline gap-3">
                <span className="font-heading text-4xl font-extrabold text-[#C1CC24]">$499</span>
                <span className="text-white/70 text-sm">AUD · 30 days of 1:1 support</span>
              </div>
              <a href={COACHING_URL} target="_blank" rel="noreferrer" data-testid="coaching-cta"
                className="inline-flex items-center gap-2 mt-7 rounded-full bg-[#C1CC24] text-[#0d2f4d] font-semibold px-8 py-4 hover:bg-[#aeb81f] transition-colors">
                View Career Coaching Package <ArrowRight size={18} />
              </a>
              <p className="text-white/50 text-xs mt-3">Book a quick strategy call to see if it's right for you — no obligation.</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                [FileUp, "Australian CV, done properly", "A recruiter-grade CV tailored to Australian employers and your target roles — not a template."],
                [BadgeCheck, "LinkedIn that gets found", "Profile rewrite and optimisation so recruiters and hiring managers actually find and shortlist you."],
                [ClipboardCheck, "Interview preparation", "Mock interviews, tough-question practice and personalised feedback so you walk in confident."],
                [Handshake, "Job-search strategy", "A clear 30-day plan — where to look, how to approach employers, salary and negotiation coaching."],
              ].map(([Icon, t, d]) => (
                <div key={t} className="rounded-lg bg-white/5 border border-white/10 p-5" data-testid="coaching-feature">
                  <Icon className="text-[#C1CC24]" size={22} />
                  <h3 className="font-heading font-bold text-white text-base mt-3">{t}</h3>
                  <p className="text-white/65 text-sm mt-1.5 leading-relaxed">{d}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="relative mt-9 border-t border-white/15 pt-6 text-xs text-white/55 leading-relaxed space-y-1.5" data-testid="coaching-disclaimer">
            <p>Registration with The Ditch Careers is free.</p>
            <p>Candidates are not required to purchase the coaching package.</p>
            <p>Purchasing coaching does not provide priority access to roles.</p>
            <p>Purchasing coaching does not guarantee representation, interviews, placement or employment.</p>
            <p>The Ditch Careers recruitment service and The Ditch Collective coaching package are separate services.</p>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-24">
        <div className="grid lg:grid-cols-2 gap-12">
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold text-[#0d2f4d] tracking-tight">What you'll need</h2>
            <p className="text-[#51637a] mt-3 max-w-md leading-relaxed">Have these handy to complete your profile quickly. You can add them anytime.</p>
          </div>
          <ul className="grid sm:grid-cols-2 gap-3 text-sm">
            {[
              { label: "Your CV (PDF or Word)" },
              { label: "A profile photo" },
              { label: "Qualifications & credentials" },
              { label: "Two references", note: "Have their contact details ready" },
              { label: "Photo ID (passport / licence)" },
              { label: "Police check (if available)" },
            ].map(({ label, note }) => (
              <li key={label} className="flex items-start gap-3 bg-white border border-[#e3e0d3] rounded-lg px-4 py-3.5 text-[#122c47]">
                <ClipboardCheck size={16} className="text-[#7C8514] shrink-0 mt-0.5" />
                <span>
                  {label}
                  {note && <span className="block text-xs text-[#51637a] mt-0.5" data-testid="references-note">{note}</span>}
                </span>
              </li>
            ))}
          </ul>
        </div>
        <div className="mt-20 grid lg:grid-cols-2 gap-12" data-testid="candidate-faq-section">
          <div>
            <p className="font-accent text-lg text-[#7C8514]">Good to know</p>
            <h2 className="text-2xl sm:text-3xl font-bold text-[#0d2f4d] tracking-tight mt-2">Questions candidates ask us</h2>
            <p className="text-[#51637a] mt-3 max-w-md leading-relaxed flex items-start gap-2">
              <ShieldCheck size={18} className="text-[#7C8514] mt-0.5 shrink-0" />
              Short version: it's private, it's personal, and a real person is looking after you the whole way.
            </p>
          </div>
          <Accordion type="single" collapsible className="w-full">
            {[
              ["Who can see my profile?", "Your profile is never public and never searchable. Only our recruiters see it. Employers only ever see candidates we hand-pick and present for their specific role — and only after we've spoken with you."],
              ["What if I don't want my current employer to know I'm looking?", "That's exactly what your blocked companies list is for. Add any employer to it and they will never see your profile or know you're in our talent pool — even if they're a client of ours."],
              ["I'm not in Australia yet — can I still sign up?", "Absolutely. We place people relocating from all over the world. We work out your right-to-work pathway, talk you through visas and timing, and line up roles before you land."],
              ["What happens after I complete my profile?", "A recruiter reviews it — a real person, not an algorithm. When there's a role that fits your skills, location and preferences, they'll reach out, talk it through with you, and only put you forward with your say-so."],
              ["Can I update my profile later?", "Any time. Log in to your dashboard to update your preferences, add documents, change your blocked list or upload a fresh CV — the CV creator will rebuild your profile from the new version."],
            ].map(([q, a], i) => (
              <AccordionItem key={q} value={`faq-${i}`} data-testid={`candidate-faq-${i}`}>
                <AccordionTrigger className="text-left font-heading font-semibold text-[#0d2f4d] hover:text-[#7C8514] hover:no-underline">{q}</AccordionTrigger>
                <AccordionContent className="text-sm text-[#51637a] leading-relaxed">{a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>

        <div className="mt-16 bg-[#0d2f4d] rounded-lg grain relative overflow-hidden p-10 sm:p-14">
          <h2 className="relative text-2xl sm:text-3xl font-bold text-white tracking-tight max-w-lg">Ready to be found by the right employer?</h2>
          <Link to="/register" data-testid="candidate-landing-cta-bottom"
            className="relative inline-flex items-center gap-2 mt-7 rounded-full bg-[#C1CC24] text-[#0d2f4d] font-medium px-8 py-4 hover:bg-[#aeb81f] transition-colors">
            Create Your Free Candidate Profile <ArrowRight size={18} />
          </Link>
          <p className="relative mt-4 text-sm text-white/70 max-w-lg">Free candidate registration. There is no fee to be considered for roles through The Ditch Careers.</p>
        </div>
      </section>
      <SiteFooter />
    </div>
  );
}
